import { useState } from "react";
import { ChevronDown, HelpCircle, ExternalLink, FlagTriangleRight } from "lucide-react";

const MODULES = [
  {
    code: "PLN",
    title: "Planning",
    description: "Planning enables you to create and analyze Statements of Work (SOWs), helping identify project risks, assess commercial viability, and ensure compliance with organizational standards.",
    formUrl:"https://docs.google.com/forms/d/e/1FAIpQLSddVO0ZW6IfVtt8gcBEE3FbfOfjWolm3dj6ltDDPFv49IkC0g/viewform?usp=dialog",
    faqs: [
      {
        q: "What are SOW rules?",
        a: [
          "SOW (Statement of Work) rules are configurable validation and scoring criteria used to evaluate the quality, completeness, and risk of a Statement of Work (SOW). Each rule checks for a specific requirement—such as clearly defined acceptance criteria, AI feasibility, or project scope—and contributes to the overall SOW analysis score.",
          "Rules can be configured based on:",
          "1. Contract Type: Fixed Bid, Time & Materials (T&M), Managed Services, etc.",
          "2. Evaluation Dimension: Acceptance Criteria, AI Feasibility, AI/ML Scope, and more.",
          "3. Severity & Weight: Determines the impact of the rule on the overall score.",
          "4. Mandatory Status: Indicates whether the rule must be satisfied.",
          "5. Active Status: Controls whether the rule is applied during SOW analysis.",
          "These rules help ensure SOWs are consistent, compliant, and ready for review by identifying missing information, potential risks, and areas that require attention before approval.",
        ],
      },
      {
        q: "What does SOW Analysis do?",
        a: [
          "SOW Analysis automatically reviews your Statement of Work (SOW) against a predefined set of business and compliance rules to assess its completeness, quality, and potential risks.",
          "The analysis helps you:",
          "• Evaluate the overall quality of your SOW with an Overall Score.",
          "• Measure Commercial, Compliance, and Risk scores independently.",
          "• Identify which requirements are Covered, Partially Covered, or Missing.",
          "• Detect missing mandatory clauses and potential contractual risks.",
          "• Analyze key areas such as scope, pricing, deliverables, security, legal terms, governance, resourcing, and compliance.",
          "• Highlight areas that require improvement and provide recommendations to strengthen the SOW before review or approval.",
          "SOW Analysis enables you to identify gaps early, improve document quality, reduce project risks, and ensure your SOW aligns with organizational standards and best practices.",
        ],
      },
      {
        q:"What is the use of SOW Template?",
        a:"The SOW Template tab allows users to manage and customize prebuilt Statement of Work (SOW) templates. You can update existing templates by adding new sections or modifying current ones, as well as create entirely new templates from scratch to meet specific project or organizational requirements."
      },
      {
        q: "How to use SOW Template?",
        a: [
          "The SOW Template tab is used to create and manage Statement of Work (SOW) templates. You can update existing templates by modifying their content or create new templates from scratch for future use.",
          "Follow these steps to create or update an SOW Template:",
          "1. Select or Create a Template:",
          ">> Choose an existing template to edit and customize.",
          ">> Or select Create New Template to build a template from scratch.",
          "2. Manage Template Sections:",
          ">> View and edit the existing sections of the selected template.",
          ">> Update section titles or content as needed.",
          "3. Customize the Template:",
          ">> Add new sections to include additional information.",
          ">> Modify or remove existing sections to match your organization's requirements.",
          "4. Create a New Template:",
          ">> Add the required sections and define the content for each section.",
          ">> Organize the template structure as needed.",
          "5. Save the Template:",
          ">> Review the template to ensure all sections and content are accurate.",
          ">> Save the template to make it available for future SOW generation."
        ],
      },
      {
        q:" What is SOW Drafting and how does it work?",
        a:[
          "SOW Drafting is a three-step AI-powered workflow that takes your project details and generates a fully structured Statement of Work document using pre-built templates. The three steps are:",
          "1. Opportunity — Select or confirm the opportunity this SOW is being drafted for.",
          "2. Details — Fill in project information, scope, resource plan, and optionally upload supporting proposal documents.",
          "3. Draft — Review, edit, regenerate, and publish the AI-generated SOW across all sections.",
        ]
      },
      {
        q:"What information do I need to fill in on the Project Details page?",
        a:[
            "The Project Details page collects everything the AI needs to generate a meaningful SOW:",
            "• Template — Select the pre-built SOW template (e.g. Template 1 with 41 sections).",
            "• Project Name & Client — Name of the engagement; Client is auto-populated from the linked account.",
            "• Start Date / End Date — The planned delivery period.",
            "• Project Context (Required) — Background, needs, and goals of the project.",
            "• In Scope (Required) — What is included in the engagement.",
            "• Out of Scope (Required) — What is explicitly excluded.",
            "• Assumptions — Conditions assumed to be true for delivery.",
            "• Deliverables — Outputs or artifacts the project will produce.",
            "• Proposal Documents (Optional) — Supporting files for additional AI context.",
            "• Resource Plan — Team roles, profiles, locations, technologies, and hours.",
        ]
      },
      {
        q:"What is the Resource Plan and how do I fill it in?",
        a:"The Resource Plan captures the staffing breakdown. Each row represents one role type with these columns: Role, Profile, Location, Technology, Domain, # (headcount), Start date, End date, and Hours. You can add rows manually using + Add Manual, or pull existing data directly from JOSS using Fetch from JOSS. Total hours are auto-summed at the bottom."
      },
      {
        q:"How do I edit a generated section in Step 3 of the Draft SOW?",
        a:"Click any section card in the grid to open it in the Edit panel at the bottom of the page. The selected section is highlighted in blue. From there you can edit the content directly, click Regenerate to get a fresh AI-generated version, or click Save to keep your changes."
      },
      {
        q:"Can I regenerate just one section without redoing the whole SOW?",
        a:" Yes. Each section has its own Regenerate button in the Edit panel. It regenerates only that section using the original project inputs — all other sections stay untouched. Useful after a scope change or if the initial output missed something."
      }
    ],
  },
  {
    code: "A&D",
    title: "Analysis & Design",
    description: "Analyzes project requirements to identify business needs, assess risks, and validate documentation for completeness and accuracy and creates the technical blueprint of your solution by defining the architecture, workflows, components, and data models.",
    formUrl:"https://docs.google.com/forms/d/e/1FAIpQLSeRDalvDi7cfmhHIbjH83mk3L0dkAmc1rgrsqD4vSYliLJZpA/viewform?usp=dialog",
    faqs: [
      {
        q: "How to generate requirements from SOW?",
        a: [
          "Requirements Studio helps you analyze a Statement of Work (SOW) and generate structured project requirements by extracting key information from the document.",
          "Follow these steps:",
          "1. Open Requirement Studio",
          "2. Create a New Project",
          ">> Enter the project details, including the project name, description, and the technologies you plan to use.",
          "3. Upload the SOW Document",
          ">> Upload your Statement of Work (SOW) to the project.",
          ">> The system automatically parses the document and extracts its content.",
          "4. Review the Parsed Information",
          ">> Once parsing is complete, review the extracted information in the following tabs:",
          ">>> Overview — Provides a summary of the detected sections in SOW.",
          ">>> Sections — Displays the content organized by SOW sections.",
          ">>> Extracted Tables — Lists all tables identified in the document.",
          ">>> Raw Text — Shows the complete extracted text from the SOW.",
          "5. Generate Requirements",
          ">> After reviewing the parsed content, click Generate to create project requirements based on the uploaded SOW.",
          "6. Review and Refine",
          ">> Review the generated requirements and make any necessary edits or refinements before saving or exporting them.",
        ],
      },
      {
        q: "How do I use the Generate tab in Requirements Studio?",
        a: [
          "The Generate tab allows you to review, refine, and manage all AI-generated project artifacts created from your uploaded Statement of Work (SOW). The generated content is organized into modules and categorized into different artifact types for easy navigation.",
          "Follow these steps:",
          "1. Generate Artifacts",
          ">> After uploading and parsing your SOW, click Generate to create AI-powered project artifacts.",
          "2. Select a Module",
          ">> Choose a module from the left panel to view the artifacts generated for that functional area.",
          "3. View Generated Artifacts",
          ">> Navigate between the available tabs to review different artifact types, including:",
          ">>> i. Requirements",
          ">>> ii. Non-Functional Requirements",
          ">>> iii. Tasks",
          ">>> iv. Test Cases",
          ">>> v. Architecture",
          ">>> vi. Risks",
          "4. Review Artifact Details",
          ">> Open an artifact to view its generated content, such as the description, user story, acceptance criteria, and other relevant details.",
          "5. Refine or Edit Artifacts",
          ">> Use the Refine option to provide additional instructions and regenerate the selected artifact.",
          ">> You can also edit individual artifacts manually to better align them with your project requirements.",
          "6. Validate and Export",
          ">> Once you have reviewed the generated artifacts, use the Validate option to verify the output.",
          ">> Export the generated artifacts or push them to integrated project management tools (if configured).",
        ],
      },
      {
        q: "What is HLD Generation?",
        a: "It's a feature that automatically generates a High-Level Design document — broken into 11 standard architecture sections — using your project's Statement of Work and your organization's service line standards as the source context.",
      },
      {
        q: "What does SOW linked and M15 Standards mean in the Generated With panel?",
        a: "These show you exactly what informed the generation: the SOW confirms the document is grounded in your actual contract scope, while M15 Standards indicates your organization's internal architecture/service line standards were applied — so naming conventions, folder layouts, and patterns stay consistent across projects.",
      },
      {
        q: "Can I regenerate just one section instead of the whole document?",
        a: "Yes. Each section has its own Regenerate button, so if only the Database Design or Folder Structure needs updating — say, after a scope change — you don't have to regenerate all 11 sections from scratch. Regenerate All is available if you do want a full refresh.",
      },
      {
        q: "Can I manually edit a generated section?",
        a: "Yes, each section includes an Edit option, so you can fine-tune the generated content directly rather than only being able to regenerate it.",
      },
    ],
  },
  {
    code: "DEP",
    title: "Deployment",
    description: "GoLive is an AI-powered deployment pipeline that takes your Git repository from source code and automatically analyses your stack, then generates ready-to-run deployment scripts tailored to your target infrastructure.",
    formUrl:"https://docs.google.com/forms/d/e/1FAIpQLSeszOSjnWPwoTK-TUlef8R-hG9m8W72jdvC9Uol8M2DpxJbZg/viewform?usp=dialog",
    faqs: [
      {
        q: "What does GoLive actually do?",
        a: "You give it a GitHub repository link. It clones and analyzes the code, figures out what kind of application it is (a Python API, a React frontend, a Java web app, etc.), and proposes a set of cloud resources needed to run it — databases, secrets, compute services, networking. Once you approve the proposal, it generates the actual infrastructure-as-code scripts to deploy it.",
      },
      {
        q: "What cloud platforms does it support?",
        a: "Currently Google Cloud Platform (GCP) for Terraform-based provisioning, and Oracle Cloud Infrastructure (OCI) for Ansible-based VM configuration.",
      },
      {
        q: "What if the generated script doesn't quite work after I approve it?",
        a: "You can request regeneration with specific feedback on what needs to change. The system incorporates your notes into the next generation attempt rather than starting from scratch.",
      },
      {
        q: "What types of applications can it handle?",
        a: "It's designed to be stack-agnostic. It has handled Python (FastAPI, Django), Node.js (React, Next.js), Java (Servlets/Tomcat), and more — detection is based on standard project structure rather than hardcoded support for specific frameworks.",
      },
      {
        q: "Is the generated infrastructure code production-ready as-is?",
        a: "It's a strong starting point reviewed by a proposal step, but like any generated code, it should be reviewed before production use — particularly around security settings (firewall rules, public access permissions) which the tool defaults conservatively but doesn't guarantee are optimal for every use case.",
      },
    ],
  },
  {
    code: "A&C",
    title: "Audit & Compliance",
    description: "IDP uses Azure OpenAI to turn Statements of Work into ready-to-use project artifacts — automatically generating a populated SMP or RAID Tracker in minutes instead of hours of manual drafting.",
    formUrl:"https://docs.google.com/forms/d/e/1FAIpQLSfkKqXV6J8HQsx6rweal8y89ohFbOmNxz2rHzdgnzd5-FVCPQ/viewform?usp=dialog",
    faqs: [
      {
        q: "How do I kick off the automated extraction process for my new project?",
        a: "It's simple. Just navigate to the Audit and Compliance dashboard, click Upload, and select your signed Statement of Work (SOW) document. The pipeline will automatically initiate and run both the SMP and RAID extraction tracks simultaneously.",
      },
      {
        q: "What does the generated SMP actually contain?",
        a: "The Service Management Plan is a full document covering project organization, roles and responsibilities, escalation matrices, communication plans, service delivery metrics (QPPO/MPO), and governance structure — sections that would normally take hours to draft manually from a SOW are extracted and pre-filled automatically.",
      },
      {
        q: "What does the generated RAID Tracker contain?",
        a: "It's a multi-tab spreadsheet with a populated Risk Register (each risk scored by probability × impact, with mitigation and contingency plans), plus dedicated tabs for Actions & Dependencies and Issues — along with reference guidelines for how risk scoring works, so the tracker is usable standalone.",
      },
      {
        q: "How are risks scored in the RAID Tracker?",
        a: "Each risk is rated on Probability (Low/Medium/High) and Impact (Low/Medium/High), multiplied together for a Risk Score, then ranked. The tool follows this same scoring methodology when generating risks from your SOW, so outputs are consistent with standard risk management practice.",
      },
      {
        q: "Can the tool identify risks that aren't explicitly labeled risk in the SOW?",
        a: `Yes. It analyzes contractual clauses, SLA terms, dependencies, and constraints throughout the document — for example, a clause about payment terms or staffing rotations can surface as a financial or operational risk, even if the SOW never uses the word "risk."`,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// How indentation works in answer arrays:
//
// Plain text          → normal paragraph, no indent
// "1. ..." or "i. …" → numbered/roman step, indent level 1
// "• ..."             → bullet point, indent level 1
// ">>"                → prefix for indent level 2 (sub-point)
// ">>>"               → prefix for indent level 3 (sub-sub-point)
//
// You can add as many ">" characters as you need for deeper nesting.
// Example: ">> Sub-point"  ">>> Sub-sub-point"  ">>>> Deep point"
// ---------------------------------------------------------------------------
function parseLine(line) {
  // Count leading >> groups for manual indent levels
  const manualMatch = line.match(/^(>+)\s+/);
  if (manualMatch) {
    const depth = manualMatch[1].length; // number of > characters
    const text = line.slice(manualMatch[0].length);
    return { type: "indent", depth, text };
  }
  if (/^\d+\.\s+/.test(line) || /^[ivxlcdmIVXLCDM]+\.\s+/.test(line)) {
    return { type: "numbered", depth: 1, text: line };
  }
  if (/^[•\-\*]\s+/.test(line)) {
    return { type: "bullet", depth: 1, text: line };
  }
  return { type: "paragraph", depth: 0, text: line };
}

function FAQItem({ q, a, isOpen, onToggle }) {
  const paragraphs = Array.isArray(a) ? a : [a];

  return (
    <div className="faq-item">
      <button className="faq-question" onClick={onToggle} aria-expanded={isOpen}>
        <span>{q}</span>
        <ChevronDown
          size={18}
          className="faq-chevron"
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      <div className="faq-answer-wrapper" style={{ maxHeight: isOpen ? "9999px" : "0px" }}>
        <div className="faq-answer">
          {paragraphs.map((line, i) => {
            const { type, depth, text } = parseLine(line);
            const indentPx = depth * 20; // 20px per level — change this to adjust all indents globally

            if (type === "paragraph") {
              return (
                <p key={i} className="faq-line faq-paragraph">
                  {text}
                </p>
              );
            }
            if (type === "numbered") {
              return (
                <p key={i} className="faq-line faq-numbered" style={{ paddingLeft: `${indentPx}px` }}>
                  {text}
                </p>
              );
            }
            if (type === "bullet") {
              return (
                <p key={i} className="faq-line faq-bullet" style={{ paddingLeft: `${indentPx}px` }}>
                  {text}
                </p>
              );
            }
            if (type === "indent") {
              return (
                <p key={i} className="faq-line faq-indent" style={{ paddingLeft: `${indentPx}px` }}>
                  {text}
                </p>
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}

function FeedbackCallout({ moduleTitle, formUrl }) {
  return (
    <a
      href={formUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="feedback-callout"
    >
      <div className="feedback-callout-icon">
        <FlagTriangleRight size={17} />
      </div>
      <div className="feedback-callout-text">
        <span className="feedback-callout-label">Found an issue in {moduleTitle}?</span>
        <span className="feedback-callout-sub">
          Help us improve — report a bug or incorrect information via our feedback form.
        </span>
      </div>
      <ExternalLink size={16} className="feedback-callout-arrow" />
    </a>
  );
}

function ModuleSection({ module, openMap, setOpenMap }) {
  const toggle = (idx) => {
    setOpenMap((prev) => ({
      ...prev,
      [module.code]: { ...prev[module.code], [idx]: !prev[module.code]?.[idx] },
    }));
  };

  return (
    <section className="module-section" id={module.code}>
      <div className="module-header">
        <span className="module-code">{module.code}</span>
        <div>
          <h2 className="module-title">{module.title}</h2>
          <p className="module-description">{module.description}</p>
        </div>
      </div>

      <div className="faq-list">
        {module.faqs.map((faq, idx) => (
          <FAQItem
            key={idx}
            q={faq.q}
            a={faq.a}
            isOpen={!!openMap[module.code]?.[idx]}
            onToggle={() => toggle(idx)}
          />
        ))}
      </div>

      <FeedbackCallout moduleTitle={module.title} formUrl={module.formUrl} />
    </section>
  );
}

export default function FAQPage() {
  const [activeModule, setActiveModule] = useState(MODULES[0].code);
  const [openMap, setOpenMap] = useState({});

  const scrollToModule = (code) => {
    setActiveModule(code);
    const el = document.getElementById(code);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="faq-page">
      <style>{`
        :root {
          --jo-blue: #3258E8;
          --jo-lavender-bg: #F3F2FA;
          --jo-border: #E6E4F0;
          --jo-ink: #1A1D2E;
          --jo-text-muted: #6B6F87;
          --jo-card-bg: #FFFFFF;
        }

        .faq-page {
          background: var(--jo-lavender-bg);
          color: var(--jo-ink);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .faq-hero {
          padding: 40px 40px 28px;
          max-width: 1000px;
        }

        .faq-eyebrow {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--jo-blue);
          margin: 0 0 10px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .faq-hero h1 {
          font-size: 30px;
          font-weight: 800;
          letter-spacing: -0.01em;
          color: var(--jo-ink);
          margin: 0 0 10px;
        }

        .faq-hero p {
          font-size: 14.5px;
          color: var(--jo-text-muted);
          max-width: 580px;
          margin: 0;
          line-height: 1.55;
        }

        .faq-module-pills {
          display: flex;
          gap: 8px;
          padding: 0 40px 28px;
          flex-wrap: wrap;
        }

        .faq-pill {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 20px;
          background: var(--jo-card-bg);
          border: 1px solid var(--jo-border);
          font-size: 13px;
          font-weight: 600;
          color: var(--jo-text-muted);
          cursor: pointer;
        }

        .faq-pill.active {
          background: var(--jo-blue);
          border-color: var(--jo-blue);
          color: #fff;
        }

        .faq-pill-code {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.04em;
          opacity: 0.75;
        }

        .faq-content {
          display: flex;
          flex-direction: column;
          gap: 36px;
          padding: 0 40px 64px;
          max-width: 880px;
        }

        .module-section {
          scroll-margin-top: 20px;
          background: var(--jo-card-bg);
          border: 1px solid var(--jo-border);
          border-radius: 14px;
          padding: 28px 28px 24px;
        }

        .module-header {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          margin-bottom: 18px;
        }

        .module-code {
          flex-shrink: 0;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.04em;
          color: var(--jo-blue);
          background: #EBEEFC;
          padding: 5px 10px;
          border-radius: 6px;
          margin-top: 4px;
        }

        .module-title {
          font-size: 19px;
          font-weight: 700;
          color: var(--jo-ink);
          margin: 0 0 4px;
        }

        .module-description {
          font-size: 13.5px;
          color: var(--jo-text-muted);
          margin: 0;
          line-height: 1.5;
        }

        .faq-list {
          border-top: 1px solid var(--jo-border);
        }

        .faq-item {
          border-bottom: 1px solid var(--jo-border);
        }

        .faq-question {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          background: none;
          border: none;
          text-align: left;
          padding: 15px 2px;
          font-size: 14.5px;
          font-weight: 600;
          color: var(--jo-ink);
          cursor: pointer;
        }

        .faq-chevron {
          flex-shrink: 0;
          color: var(--jo-text-muted);
          transition: transform 0.2s ease;
        }

        .faq-answer-wrapper {
          overflow: hidden;
          transition: max-height 0.4s ease;
        }

        .faq-answer {
          padding: 4px 2px 18px;
        }

        /* Base style for every answer line */
        .faq-line {
          font-size: 13.5px;
          line-height: 1.65;
          color: var(--jo-text-muted);
          margin: 0 0 5px;
        }

        .faq-line:last-child { margin-bottom: 0; }

        /* Plain paragraph — no indent */
        .faq-paragraph {}

        /* Numbered step — indent applied inline via style prop */
        .faq-numbered {}

        /* Bullet point — indent applied inline via style prop */
        .faq-bullet {}

        /* Manual >> indent lines */
        .faq-indent {
          color: var(--jo-text-muted);
        }

        .feedback-callout {
          margin-top: 20px;
          display: flex;
          align-items: center;
          gap: 13px;
          padding: 14px 16px;
          background: #EBEEFC;
          border: 1px solid #D5DBF7;
          border-radius: 10px;
          text-decoration: none;
          transition: background 0.15s ease, border-color 0.15s ease;
        }

        .feedback-callout:hover {
          background: #DDE3FA;
          border-color: var(--jo-blue);
        }

        .feedback-callout-icon {
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--jo-blue);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .feedback-callout-text {
          display: flex;
          flex-direction: column;
          gap: 1px;
          flex: 1;
        }

        .feedback-callout-label {
          font-size: 13.5px;
          font-weight: 700;
          color: var(--jo-ink);
        }

        .feedback-callout-sub {
          font-size: 12.5px;
          color: var(--jo-text-muted);
        }

        .feedback-callout-arrow {
          flex-shrink: 0;
          color: var(--jo-blue);
        }

        @media (max-width: 640px) {
          .faq-hero, .faq-module-pills, .faq-content {
            padding-left: 20px;
            padding-right: 20px;
          }
        }
      `}</style>

      <div className="faq-hero">
        <p className="faq-eyebrow">
          <HelpCircle size={13} /> Help Center
        </p>
        <h1>Frequently Asked Questions</h1>
        <p>
          Answers organized by project phase. If you spot an error, an outdated
          answer, or a bug anywhere in the product, use the feedback form at
          the end of each section to report it.
        </p>
      </div>

      <div className="faq-module-pills">
        {MODULES.map((m) => (
          <button
            key={m.code}
            className={`faq-pill ${activeModule === m.code ? "active" : ""}`}
            onClick={() => scrollToModule(m.code)}
          >
            <span className="faq-pill-code">{m.code}</span>
            <span>{m.title}</span>
          </button>
        ))}
      </div>

      <div className="faq-content">
        {MODULES.map((module) => (
          <ModuleSection key={module.code} module={module} openMap={openMap} setOpenMap={setOpenMap} />
        ))}
      </div>
    </div>
  );
}